// GENERATED FILE - do not edit by hand.
// Produced by blender/scripts/gen_maze_spec.py from blender/spec/maze.json.
// Run `npm run spec` to refresh it; tests/verify.mjs fails if it is stale.
//
// This is the SINGLE SOURCE OF TRUTH for the board's geometry, shared with the
// Blender build. The models and the Rapier colliders are both generated from
// these records, which is what guarantees that a wall the ball collides with is
// a wall the player can see - and that a wall the player can see is one the ball
// collides with. Both halves of that rule have been broken in this project's
// history; the shared spec is what makes them impossible now.
//
// Board frame (see docs/TECHNICAL_DESIGN.md):
//   +Y up on screen, +Z out of the board toward the camera, gravity (0, -9.81, 0)
//   The assembly rotates about Z. Angles are CCW from +X: 90 deg is the top of
//   the board, 270 deg the bottom. Angles are degrees, lengths are metres.


/** An angular gap in a ring: the ball can pass through this arc. */
export interface MazeOpeningSpec {
  readonly centerDeg: number;
  readonly halfWidthDeg: number;
}

/** A solid arc, as a pair of angles. May run past 360 for a span across 0. */
export interface MazeSpanSpec {
  readonly startDeg: number;
  readonly endDeg: number;
}

/** A radial band, used both for a wall's solid parts and for its gaps. */
export interface MazeRadialBandSpec {
  readonly fromRadius: number;
  readonly toRadius: number;
}

/** The free radial band between two bounding rings, or the hub and the guard. */
export interface MazeChannelSpec {
  readonly id: string;
  readonly innerRadius: number;
  readonly outerRadius: number;
}

/** One concentric rail. A ring with no openings is a complete circle. */
export interface MazeRingSpec {
  readonly id: string;
  readonly radius: number;
  readonly thickness: number;
  readonly segments: number;
  readonly material: string;
  readonly emissive: number;
  readonly openings: readonly MazeOpeningSpec[];
  readonly solidSpans: readonly MazeSpanSpec[];
}

/**
 * One radial wall.
 *
 * `solidBands` are the parts that actually exist: the channel minus `gaps`.
 * The ball cannot pass a solid band by rotating - rotation does not change its
 * radius - so a wall is only passable through a ring opening or one of its own
 * gaps.
 */
export interface MazeRadialWallSpec {
  readonly id: string;
  readonly channelId: string;
  readonly innerRadius: number;
  readonly outerRadius: number;
  readonly centerDeg: number;
  readonly thickness: number;
  readonly material: string;
  readonly gaps: readonly MazeRadialBandSpec[];
  readonly solidBands: readonly MazeRadialBandSpec[];
}

/** A short plate protruding from one wall of a channel, to turn the ball. */
export interface MazeDeflectorSpec {
  readonly id: string;
  readonly channelId: string;
  readonly innerRadius: number;
  readonly outerRadius: number;
  /** Which wall the plate is attached to. It reaches `length` in from there. */
  readonly from: 'inner' | 'outer';
  readonly centerDeg: number;
  readonly radius: number;
  readonly length: number;
  readonly skewDeg: number;
  readonly thickness: number;
  readonly material: string;
}

/** A diagonal chute carrying the ball across part of its channel. */
export interface MazeRampSpec {
  readonly id: string;
  readonly channelId: string;
  readonly innerRadius: number;
  readonly outerRadius: number;
  readonly startDeg: number;
  readonly sweepDeg: number;
  /** Where the diagonal starts, across the channel: 0 inner face, 1 outer face. */
  readonly atFraction: number;
  /** How far it crosses over `sweepDeg`. Negative climbs inward. */
  readonly rise: number;
  /** Radius the diagonal starts at, i.e. `atFraction` resolved. */
  readonly atRadius: number;
  /** Radius the diagonal ends at, i.e. `atFraction + rise` resolved. */
  readonly endRadius: number;
  readonly thickness: number;
  readonly material: string;
}

/** A peg standing in a channel. */
export interface MazeObstacleSpec {
  readonly id: string;
  readonly channelId: string;
  readonly innerRadius: number;
  readonly outerRadius: number;
  readonly centerDeg: number;
  readonly radius: number;
  readonly pegRadius: number;
  readonly material: string;
}

/**
 * One transparent arc of the outer guard.
 *
 * The guard is the exit. The ball leaves the board by rolling along an arc's
 * inner face and off the edge of an opening, so an opening's edge is what aims
 * the shot: the edge's world angle is `centerDeg +/- halfWidthDeg + boardAngle`.
 */
export interface MazeGuardArcSpec {
  readonly id: string;
  readonly startDeg: number;
  readonly endDeg: number;
}

/** A gap in the guard. The one at the bottom is the exit window. */
export interface MazeGuardOpeningSpec {
  readonly id: string;
  readonly centerDeg: number;
  readonly halfWidthDeg: number;
}

export interface MazeGuardSpec {
  readonly radius: number;
  readonly tube: number;
  readonly segments: number;
  readonly openings: readonly MazeGuardOpeningSpec[];
  /** Derived: the complement of `openings`. */
  readonly arcs: readonly MazeGuardArcSpec[];
}

export interface MazeBoardSpec {
  readonly outerRadius: number;
  readonly plateRadius: number;
  readonly plateThickness: number;
  readonly plateFrontZ: number;
  readonly wallZ: number;
  readonly wallDepth: number;
  readonly coverZ: number;
  readonly coverThickness: number;
  readonly hubRadius: number;
  readonly hubHeight: number;
  readonly guardTube: number;
}

/**
 * Where the ball is released, in WORLD space.
 *
 * Fixed in world space, not board space: the board is rotated during the
 * countdown, and the ball always drops at the top of the screen.
 */
export interface MazeSpawnSpec {
  readonly angleDeg: number;
  /** A small offset so the ball does not balance on the apex. */
  readonly angleOffsetDeg: number;
  /** Radius, derived as the midpoint of the outermost channel. */
  readonly radius: number;
}

export interface MazeBallSpec {
  readonly radius: number;
  /** Rapier's predictive contact margin: the effective radius is radius + this. */
  readonly contactSkin: number;
  /** Play the ball needs on top of its diameter to pass an opening. */
  readonly laneClearance: number;
  readonly spawn: MazeSpawnSpec;
}

/** One pocket in the fixed row along the bottom edge of the machine. */
export interface MazeSlotSpec {
  readonly id: string;
  readonly index: number;
  /** 0 is the centre pocket; n is the pair n steps out either side. */
  readonly tier: number;
  readonly centerX: number;
  readonly halfWidth: number;
  readonly points: number;
  readonly label: string;
}

export interface MazeSlotTierSpec {
  readonly points: number;
  readonly label: string;
}

/**
 * The scoring row.
 *
 * Fixed to the cabinet: it does NOT rotate with the board. `mouthY` is the world
 * Y the ball's centre crosses on its way in, and that crossing is resolved from
 * the ball's X alone - never from a mesh name, a material or a pixel colour.
 */
export interface MazeSlotsSpec {
  readonly count: number;
  readonly spanRadius: number;
  readonly mouthY: number;
  readonly depth: number;
  readonly backZ: number;
  readonly frontZ: number;
  readonly dividerThickness: number;
  readonly tiers: readonly MazeSlotTierSpec[];
  readonly entries: readonly MazeSlotSpec[];
}

export interface MazeSpec {
  readonly version: number;
  readonly units: string;
  readonly board: MazeBoardSpec;
  readonly ball: MazeBallSpec;
  readonly guard: MazeGuardSpec;
  readonly rings: readonly MazeRingSpec[];
  readonly channels: readonly MazeChannelSpec[];
  readonly radialWalls: readonly MazeRadialWallSpec[];
  readonly deflectors: readonly MazeDeflectorSpec[];
  readonly ramps: readonly MazeRampSpec[];
  readonly obstacles: readonly MazeObstacleSpec[];
  readonly slots: MazeSlotsSpec;
}


/** The board. Everything here is in the board frame. */
const BOARD: MazeBoardSpec = {
  outerRadius: 1.15,
  plateRadius: 1.25,
  plateThickness: 0.1,
  plateFrontZ: 0,
  wallZ: 0.045,
  wallDepth: 0.12,
  coverZ: 0.14,
  coverThickness: 0.04,
  hubRadius: 0.065,
  hubHeight: 0.08,
  guardTube: 0.055,
};

const BALL: MazeBallSpec = {
  radius: 0.035,
  contactSkin: 0.005,
  laneClearance: 0.04,
  spawn: {
    angleDeg: 90,
    angleOffsetDeg: -8,
    radius: 1.02925,
  },
};

/** The transparent guard: the outer boundary, and the exit. */
const GUARD: MazeGuardSpec = {
  radius: 1.15,
  tube: 0.055,
  segments: 72,
  openings: [
    {
      id: 'exit',
      centerDeg: 270,
      halfWidthDeg: 30,
    },
  ],
  arcs: [
    {
      id: 'guard_arc_0',
      startDeg: 300,
      endDeg: 600,
    },
  ],
};

/** Concentric rails, innermost first. */
const RINGS: readonly MazeRingSpec[] = [
  {
    id: 'ring_026',
    radius: 0.26,
    thickness: 0.032,
    segments: 32,
    material: 'M_plastic_yellow',
    emissive: 1.25,
    openings: [
      {
  centerDeg: 14,
  halfWidthDeg: 16,
},
      {
  centerDeg: 320,
  halfWidthDeg: 16,
},
    ],
    solidSpans: [
      {
  startDeg: 30,
  endDeg: 304,
},
      {
  startDeg: 336,
  endDeg: 358,
},
    ],
  },
  {
    id: 'ring_048',
    radius: 0.48,
    thickness: 0.032,
    segments: 40,
    material: 'M_plastic_yellow',
    emissive: 1.15,
    openings: [
      {
  centerDeg: 22,
  halfWidthDeg: 16,
},
      {
  centerDeg: 300,
  halfWidthDeg: 16,
},
    ],
    solidSpans: [
      {
  startDeg: 38,
  endDeg: 284,
},
      {
  startDeg: 316,
  endDeg: 366,
},
    ],
  },
  {
    id: 'ring_070',
    radius: 0.7,
    thickness: 0.032,
    segments: 48,
    material: 'M_plastic_blue',
    emissive: 1.15,
    openings: [
      {
  centerDeg: 50,
  halfWidthDeg: 16,
},
      {
  centerDeg: 280,
  halfWidthDeg: 16,
},
    ],
    solidSpans: [
      {
  startDeg: 66,
  endDeg: 264,
},
      {
  startDeg: 296,
  endDeg: 394,
},
    ],
  },
  {
    id: 'ring_092',
    radius: 0.92,
    thickness: 0.032,
    segments: 56,
    material: 'M_plastic_red',
    emissive: 1.15,
    openings: [
      {
  centerDeg: 78,
  halfWidthDeg: 16,
},
      {
  centerDeg: 258,
  halfWidthDeg: 16,
},
    ],
    solidSpans: [
      {
  startDeg: 94,
  endDeg: 242,
},
      {
  startDeg: 274,
  endDeg: 422,
},
    ],
  },
];

/** The free radial bands between consecutive rings. */
const CHANNELS: readonly MazeChannelSpec[] = [
  {
    id: 'hub..ring_026',
    innerRadius: 0.065,
    outerRadius: 0.244,
  },
  {
    id: 'ring_026..ring_048',
    innerRadius: 0.276,
    outerRadius: 0.464,
  },
  {
    id: 'ring_048..ring_070',
    innerRadius: 0.496,
    outerRadius: 0.684,
  },
  {
    id: 'ring_070..ring_092',
    innerRadius: 0.716,
    outerRadius: 0.904,
  },
  {
    id: 'ring_092..guard',
    innerRadius: 0.936,
    outerRadius: 1.1225,
  },
];

/** Segmented radial walls. None of these is a full spoke. */
const RADIAL_WALLS: readonly MazeRadialWallSpec[] = [
  {
    id: 'rw_o092_a',
    channelId: 'ring_092..guard',
    innerRadius: 0.936,
    outerRadius: 1.1225,
    centerDeg: 84,
    thickness: 0.035,
    material: 'M_plastic_red',
    gaps: [
      {
  fromRadius: 0.936,
  toRadius: 1.06282,
},
    ],
    solidBands: [
      {
  fromRadius: 1.06282,
  toRadius: 1.1225,
},
    ],
  },
  {
    id: 'rw_o092_b',
    channelId: 'ring_092..guard',
    innerRadius: 0.936,
    outerRadius: 1.1225,
    centerDeg: 200,
    thickness: 0.035,
    material: 'M_plastic_red',
    gaps: [
      {
  fromRadius: 0.936,
  toRadius: 1.06282,
},
    ],
    solidBands: [
      {
  fromRadius: 1.06282,
  toRadius: 1.1225,
},
    ],
  },
  {
    id: 'rw_o070_a',
    channelId: 'ring_070..ring_092',
    innerRadius: 0.716,
    outerRadius: 0.904,
    centerDeg: 56,
    thickness: 0.035,
    material: 'M_plastic_red',
    gaps: [
      {
  fromRadius: 0.716,
  toRadius: 0.84384,
},
    ],
    solidBands: [
      {
  fromRadius: 0.84384,
  toRadius: 0.904,
},
    ],
  },
  {
    id: 'rw_o070_b',
    channelId: 'ring_070..ring_092',
    innerRadius: 0.716,
    outerRadius: 0.904,
    centerDeg: 250,
    thickness: 0.035,
    material: 'M_plastic_red',
    gaps: [
      {
  fromRadius: 0.716,
  toRadius: 0.84384,
},
    ],
    solidBands: [
      {
  fromRadius: 0.84384,
  toRadius: 0.904,
},
    ],
  },
  {
    id: 'rw_o048_a',
    channelId: 'ring_048..ring_070',
    innerRadius: 0.496,
    outerRadius: 0.684,
    centerDeg: 28,
    thickness: 0.035,
    material: 'M_plastic_blue',
    gaps: [
      {
  fromRadius: 0.496,
  toRadius: 0.62384,
},
    ],
    solidBands: [
      {
  fromRadius: 0.62384,
  toRadius: 0.684,
},
    ],
  },
  {
    id: 'rw_o048_b',
    channelId: 'ring_048..ring_070',
    innerRadius: 0.496,
    outerRadius: 0.684,
    centerDeg: 232,
    thickness: 0.035,
    material: 'M_plastic_blue',
    gaps: [
      {
  fromRadius: 0.496,
  toRadius: 0.62384,
},
    ],
    solidBands: [
      {
  fromRadius: 0.62384,
  toRadius: 0.684,
},
    ],
  },
  {
    id: 'rw_o026_a',
    channelId: 'ring_026..ring_048',
    innerRadius: 0.276,
    outerRadius: 0.464,
    centerDeg: 10,
    thickness: 0.03,
    material: 'M_plastic_purple',
    gaps: [
      {
  fromRadius: 0.276,
  toRadius: 0.40384,
},
    ],
    solidBands: [
      {
  fromRadius: 0.40384,
  toRadius: 0.464,
},
    ],
  },
  {
    id: 'rw_o026_b',
    channelId: 'ring_026..ring_048',
    innerRadius: 0.276,
    outerRadius: 0.464,
    centerDeg: 212,
    thickness: 0.03,
    material: 'M_plastic_purple',
    gaps: [
      {
  fromRadius: 0.276,
  toRadius: 0.40384,
},
    ],
    solidBands: [
      {
  fromRadius: 0.40384,
  toRadius: 0.464,
},
    ],
  },
];

/** Plates protruding from a wall, to turn the ball. */
const DEFLECTORS: readonly MazeDeflectorSpec[] = [
  {
    id: 'def_o092_a',
    channelId: 'ring_092..guard',
    innerRadius: 0.936,
    outerRadius: 1.1225,
    from: 'outer',
    centerDeg: 40,
    radius: 1.0925,
    length: 0.06,
    skewDeg: -40,
    thickness: 0.03,
    material: 'M_neon_cyan',
  },
  {
    id: 'def_o070_a',
    channelId: 'ring_070..ring_092',
    innerRadius: 0.716,
    outerRadius: 0.904,
    from: 'outer',
    centerDeg: 14,
    radius: 0.874,
    length: 0.06,
    skewDeg: -40,
    thickness: 0.03,
    material: 'M_neon_cyan',
  },
  {
    id: 'def_o048_a',
    channelId: 'ring_048..ring_070',
    innerRadius: 0.496,
    outerRadius: 0.684,
    from: 'outer',
    centerDeg: 340,
    radius: 0.654,
    length: 0.06,
    skewDeg: 30,
    thickness: 0.03,
    material: 'M_neon_magenta',
  },
  {
    id: 'def_o026_a',
    channelId: 'ring_026..ring_048',
    innerRadius: 0.276,
    outerRadius: 0.464,
    from: 'outer',
    centerDeg: 312,
    radius: 0.4365,
    length: 0.055,
    skewDeg: 30,
    thickness: 0.028,
    material: 'M_neon_magenta',
  },
];

/** Diagonal chutes that carry the ball across a channel. */
const RAMPS: readonly MazeRampSpec[] = [
  {
    id: 'ramp_o092_a',
    channelId: 'ring_092..guard',
    innerRadius: 0.936,
    outerRadius: 1.1225,
    startDeg: 150,
    sweepDeg: 30,
    atFraction: 1,
    rise: -0.35,
    atRadius: 1.1225,
    endRadius: 1.057225,
    thickness: 0.03,
    material: 'M_plastic_orange',
  },
  {
    id: 'ramp_o070_a',
    channelId: 'ring_070..ring_092',
    innerRadius: 0.716,
    outerRadius: 0.904,
    startDeg: 120,
    sweepDeg: 30,
    atFraction: 1,
    rise: -0.35,
    atRadius: 0.904,
    endRadius: 0.8382,
    thickness: 0.03,
    material: 'M_plastic_orange',
  },
];

/** Pegs standing in the channels. */
const OBSTACLES: readonly MazeObstacleSpec[] = [
  {
    id: 'obs_o092_a',
    channelId: 'ring_092..guard',
    innerRadius: 0.936,
    outerRadius: 1.1225,
    centerDeg: 20,
    radius: 1.0852,
    pegRadius: 0.028,
    material: 'M_chrome',
  },
  {
    id: 'obs_o070_a',
    channelId: 'ring_070..ring_092',
    innerRadius: 0.716,
    outerRadius: 0.904,
    centerDeg: 340,
    radius: 0.8664,
    pegRadius: 0.028,
    material: 'M_chrome',
  },
  {
    id: 'obs_o048_a',
    channelId: 'ring_048..ring_070',
    innerRadius: 0.496,
    outerRadius: 0.684,
    centerDeg: 200,
    radius: 0.6558,
    pegRadius: 0.026,
    material: 'M_chrome',
  },
  {
    id: 'obs_o026_a',
    channelId: 'ring_026..ring_048',
    innerRadius: 0.276,
    outerRadius: 0.464,
    centerDeg: 300,
    radius: 0.4264,
    pegRadius: 0.024,
    material: 'M_chrome',
  },
];

/** The scoring row: fixed to the cabinet, never rotated with the board. */
const SLOTS: MazeSlotsSpec = {
  count: 7,
  spanRadius: 1.35,
  mouthY: -1.3,
  depth: 0.26,
  backZ: -0.06,
  frontZ: 0.1,
  dividerThickness: 0.028,
  tiers: [
    {
      points: 500,
      label: '獎金',
    },
    {
      points: 250,
      label: '高分',
    },
    {
      points: 150,
      label: '中分',
    },
    {
      points: 100,
      label: '低分',
    },
  ],
  entries: [
    {
      id: 'slot_t3l',
      index: 0,
      tier: 3,
      centerX: -1.157142857,
      halfWidth: 0.192857143,
      points: 100,
      label: '低分',
    },
    {
      id: 'slot_t2l',
      index: 1,
      tier: 2,
      centerX: -0.771428571,
      halfWidth: 0.192857143,
      points: 150,
      label: '中分',
    },
    {
      id: 'slot_t1l',
      index: 2,
      tier: 1,
      centerX: -0.385714286,
      halfWidth: 0.192857143,
      points: 250,
      label: '高分',
    },
    {
      id: 'slot_t0',
      index: 3,
      tier: 0,
      centerX: 0,
      halfWidth: 0.192857143,
      points: 500,
      label: '獎金',
    },
    {
      id: 'slot_t1r',
      index: 4,
      tier: 1,
      centerX: 0.385714286,
      halfWidth: 0.192857143,
      points: 250,
      label: '高分',
    },
    {
      id: 'slot_t2r',
      index: 5,
      tier: 2,
      centerX: 0.771428571,
      halfWidth: 0.192857143,
      points: 150,
      label: '中分',
    },
    {
      id: 'slot_t3r',
      index: 6,
      tier: 3,
      centerX: 1.157142857,
      halfWidth: 0.192857143,
      points: 100,
      label: '低分',
    },
  ],
};

/** Everything above, in one place. */
export const MAZE_SPEC: MazeSpec = {
  version: 1,
  units: 'metres',
  board: BOARD,
  ball: BALL,
  guard: GUARD,
  rings: RINGS,
  channels: CHANNELS,
  radialWalls: RADIAL_WALLS,
  deflectors: DEFLECTORS,
  ramps: RAMPS,
  obstacles: OBSTACLES,
  slots: SLOTS,
};
